import { useSuspenseQuery } from '@apollo/client';
import upperFirst from 'lodash/upperFirst';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SubmittablePromptSchema } from '@eluve/api-contract';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Badge,
  Grid,
  Heading,
  Icon,
  LinkButton,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  dynamicArtifactTemplateSchema,
  hydrateDynamicArtifactTemplatePreview,
} from '@eluve/llm-outputs';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import {
  getTemplateBundleByIdQuery,
  getTemplateBundleDisplayName,
} from '../../template-bundles/template-bundle.operations';

/**
 * Tenant-scoped read view of a template bundle owned by an organization
 * (created in the tenant template editor). Unlike the global detail page it
 * drops global-only controls (default-for-new-tenants, add-organizations,
 * cross-tenant usage) and instead surfaces the owning org and a link into the
 * tenant editor where the bundle is actually managed.
 */
export const AdminTenantBundleDetailPage: React.FC = () => {
  const { bundleId } = useParams() as { bundleId: string };
  const tenantId = useTenantIdFromParams()!;
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: { llmModelPromptTemplates: [bundle] = [] },
  } = useSuspenseQuery(getTemplateBundleByIdQuery, {
    variables: { id: bundleId },
    context: eluveAdminHasuraContext,
  });

  const manageHref = `/admin/organizations/${tenantId}/manage/template-bundles`;

  // This page only renders bundles OWNED by the tenant in the URL. A bundle id
  // belonging to another org (or a global bundle) must not render under this
  // tenant's context, which would also build the "open in editor" link with the
  // wrong tenant id — treat it as not found.
  const belongsToTenant = bundle?.output_template?.tenantId === tenantId;

  const notFoundHandled = useRef(false);
  useEffect(() => {
    if (!belongsToTenant && !notFoundHandled.current) {
      notFoundHandled.current = true;
      toast.error('Template bundle not found');
      navigate(manageHref, { replace: true });
    }
  }, [belongsToTenant, navigate, toast, manageHref]);

  if (!bundle || !belongsToTenant) {
    return null;
  }

  const promptVariant = bundle.template?.prompt_template_variants?.[0];
  const outputVariant = bundle.output_template?.variants?.[0];
  const promptName = bundle.template?.name ?? 'Unknown Prompt';
  const modelName =
    bundle.model_args?.name ?? bundle.model_args?.modelType ?? 'Unknown Model';
  const outputName = bundle.output_template?.name ?? 'None';
  const displayName = getTemplateBundleDisplayName(bundle);
  const ownerName = bundle.output_template?.tenant?.name;
  const outputTemplateId = bundle.output_template?.id;

  let promptParts: { role: string; content: string }[] = [];
  if (promptVariant?.template) {
    try {
      promptParts = SubmittablePromptSchema.parse(promptVariant.template);
    } catch {
      // fallback handled below
    }
  }

  const hydratedOutputTemplate = (() => {
    if (!outputVariant?.template) return null;
    try {
      const parsed = dynamicArtifactTemplateSchema.parse(
        outputVariant.template,
      );
      return hydrateDynamicArtifactTemplatePreview(parsed);
    } catch {
      return null;
    }
  })();

  return (
    <Stack gap={6}>
      <Stack gap={2}>
        <LinkButton variant="ghost" size="sm" to={manageHref} className="w-fit">
          <Icon name="ArrowLeft" size="auto" />
          Back to organization templates
        </LinkButton>
        <Stack gap={3} align="center" direction="horizontal">
          <Heading>{displayName}</Heading>
          <Badge variant="outline">
            {ownerName ?? 'Organization template'}
          </Badge>
          {outputTemplateId && (
            <LinkButton
              variant="outline"
              size="sm"
              to={`/organizations/${tenantId}/settings/templates/bundles/${outputTemplateId}`}
            >
              Open in template editor
            </LinkButton>
          )}
        </Stack>
        <Text size="sm" color="tertiary">
          This template is owned by the organization and is managed from the
          tenant template editor.
        </Text>
      </Stack>

      <Grid columns={1} gap={6} className="md:grid-cols-10">
        {/* Prompt Template - 4/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-4"
        >
          <Stack
            justify="between"
            align="center"
            gap={2}
            direction="horizontal"
          >
            <Heading size="3xl">Prompt Template</Heading>
            {bundle.promptTemplateId && (
              <LinkButton
                variant="outline"
                size="sm"
                to={`/admin/prompts/${bundle.promptTemplateId}`}
              >
                View
              </LinkButton>
            )}
          </Stack>
          <Text size="md" weight="semibold">
            {promptName}
          </Text>
          {bundle.template?.outputType && (
            <Text size="xs" color="tertiary">
              Output Type: {bundle.template.outputType}
            </Text>
          )}
          {promptParts.length > 0 && (
            <Stack gap={4} align="stretch" className="mt-2">
              <Text size="xs" color="secondary">
                Variant {String(promptVariant?.variantNumber)} (Current Default)
              </Text>
              {promptParts.map((part) => (
                <Stack align="stretch" key={part.role} gap={2}>
                  <Heading size="lg">{`${upperFirst(part.role)} Message`}</Heading>
                  <div className="max-h-96 overflow-y-auto rounded-md border border-border-primary bg-bg-secondary p-5">
                    <pre className="text-sm whitespace-pre-wrap">
                      {part.content}
                    </pre>
                  </div>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        {/* Output Template - 4/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-4"
        >
          <Heading size="3xl">Output Template</Heading>
          <Text size="md" weight="semibold">
            {outputName}
          </Text>
          {bundle.output_template?.llmOutputType && (
            <Text size="xs" color="tertiary">
              Output Type: {bundle.output_template.llmOutputType}
            </Text>
          )}
          {hydratedOutputTemplate && (
            <Stack gap={1} align="stretch" className="mt-2">
              <Text size="xs" color="secondary">
                Variant {String(outputVariant?.variantNumber)} (Current Default)
              </Text>
              <div className="max-h-[800px] overflow-y-auto rounded-lg bg-bg-primary p-4">
                <DynamicArtifactRenderer
                  blocks={hydratedOutputTemplate.blocks ?? []}
                  isReadonly
                />
              </div>
            </Stack>
          )}
        </Stack>

        {/* Model - 2/10 */}
        <Stack
          gap={2}
          className="rounded-lg border border-border-secondary bg-bg-secondary p-4 md:col-span-2"
        >
          <Heading size="3xl">Model</Heading>
          <Text size="md" weight="semibold">
            {modelName}
          </Text>
          {bundle.model_args?.modelType && (
            <Text size="xs" color="tertiary">
              Model Type: {bundle.model_args.modelType}
            </Text>
          )}
          {bundle.model_args?.args && (
            <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-bg-quaternary p-3 text-xs text-wrap break-all text-text-secondary">
              {JSON.stringify(bundle.model_args.args, null, 2)}
            </pre>
          )}
        </Stack>
      </Grid>
    </Stack>
  );
};
